import React, { useState, useEffect } from 'react';
import { Alert, Box, Button, Typography, CircularProgress, Paper } from '@mui/material';
import { toast } from 'react-toastify';
import { getFinancialReport } from './api/underwritingApi';
import ReportViewer from './MarkdownReport.js';
import DnbFinancialReportDashboard from './DnbFinancialReportDashboard';
import {
  createDnbFinancialReportPdf,
  createGenericFinancialReportPdf,
  parseDnbFinancialReport,
} from './utils/dnbFinancialReport';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FinancialStatementsAction from './components/FinancialStatementsAction';

function FinancialReport({
  tenantId, effectiveCompanyId, refreshKey = 0, onGetFinancials,
  financialsDisabledReason = '', isGettingFinancials = false,
  canViewFinancialData = true, canDownloadReports = true,
}) {
  const [financialReport, setFinancialReport] = useState("");
  const [reportSource, setReportSource] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setFinancialReport('');
    setReportSource(null);
    setLoadError(false);
    const fetchFinancialReport = async () => {
      if (tenantId && effectiveCompanyId && canViewFinancialData) {
        setIsLoading(true);
        try {
          const result = await getFinancialReport(tenantId, effectiveCompanyId);
          if (!cancelled) {
            setFinancialReport(result?.data?.report || "");
            setReportSource(result?.data?.source || null);
          }
        } catch (error) {
          if (!cancelled) setLoadError(error?.response?.status !== 404);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchFinancialReport();
    return () => { cancelled = true; };
  }, [tenantId, effectiveCompanyId, refreshKey, retryKey, canViewFinancialData]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      let logoBytes = null;
      try {
        const response = await fetch('/logo.png');
        if (response.ok) logoBytes = await response.arrayBuffer();
      } catch (error) {
        console.warn('Could not load the CrediArc logo for the PDF', error);
      }

      const isDnb = reportSource?.provider === 'D&B';
      const parsed = isDnb ? parseDnbFinancialReport(financialReport) : null;
      const bytes = isDnb
        ? await createDnbFinancialReportPdf({ report: parsed, logoBytes })
        : await createGenericFinancialReportPdf({ markdownText: financialReport, logoBytes });
      const companyName = parsed?.companyName || 'Company';
      const safeName = companyName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'Company';
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CrediArc-${safeName}-Financial-Report.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Branded financial report downloaded.');
    } catch (error) {
      console.error('Error exporting financial report', error);
      toast.error('Failed to download the financial report.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box aria-busy={isLoading || isGettingFinancials}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: '#1A2A56' }}>
          Financial Analysis
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {financialReport && !isLoading && canViewFinancialData && canDownloadReports && (
            <Button
              variant="outlined"
              onClick={handleExport}
              disabled={isExporting}
              startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: '8px',
              }}
            >
              {isExporting ? 'Preparing PDF…' : 'Download PDF'}
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <FinancialStatementsAction
          onGetFinancials={onGetFinancials}
          disabledReason={financialsDisabledReason}
          isLoading={isGettingFinancials}
          disabled={isLoading || !canViewFinancialData}
        />
      </Box>

      {!canViewFinancialData ? (
        <Alert severity="info">Your role does not permit viewing financial reports.</Alert>
      ) : isLoading ? (
        <Box role="status" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading financial report" />
          <Typography sx={{ ml: 2 }}>Loading Financial Report...</Typography>
        </Box>
      ) : loadError ? (
        <Alert severity="error" action={<Button color="inherit" onClick={() => setRetryKey((value) => value + 1)}>Retry loading report</Button>}>
          The financial report could not be loaded. Try loading it again.
        </Alert>
      ) : financialReport ? (
        reportSource?.provider === 'D&B' ? (
          <DnbFinancialReportDashboard markdownText={financialReport} source={reportSource} />
        ) : reportSource?.provider === 'Public web research' ? (
          <Paper elevation={2} sx={{ p: 3, borderRadius: '8px', backgroundColor: '#fff', border: '1px solid #DCE8FB' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A2A56', mb: 0.5 }}>Public filings & financial trend</Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>Latest annual filings first, with an optional current-period update. Use the Open filing links to inspect the primary-source documents.</Typography>
            <ReportViewer markdownText={financialReport} tenantId={tenantId} />
          </Paper>
        ) : (
          <Paper elevation={2} sx={{ p: 3, borderRadius: '8px', backgroundColor: '#fff' }}>
            <ReportViewer markdownText={financialReport} tenantId={tenantId} />
          </Paper>
        )
      ) : (
        <Paper elevation={0} sx={{ textAlign: 'center', p: 4, border: '1px dashed #ccc', borderRadius: '8px', backgroundColor: '#fafafa' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>No Financial Report Available</Typography>
          <Typography variant="body2" color="text.secondary">
            {financialsDisabledReason || !onGetFinancials ? 'No saved financial report is available for this company.'
              : 'Select "Get financials" to retrieve available financial statements.'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default FinancialReport;
